import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getModulePermissions } from '../../utils/permissionHelper';
import { logAuditEvent } from '../../utils/auditLogger';
import { getStorageValues } from '../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../utils/downloadExportedFiles';
import VehicleGrid from './VehicleGrid';
import VehicleModal from './VehicleModal';
import VehicleModel from './models/VehicleModel';

function VehicleMain(props) {
  const [vehicleData, setVehicleData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const [modalIsOpen, setIsOpen] = useState(false);
  const moduleName = props.Module || 'Vehicle Entry';

  useEffect(() => {
    const perms = getModulePermissions(props.Module);
    setUserModulePermission(perms ? perms : []);
    fetchVehicles();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Vehicle Entry');
  }, [props.Module]);

  const fetchVehicles = async () => {
    try {
      const userId = getStorageValues('userId') || 1;
      const response = await axios.post(getApiBaseUrl() + '/vehicle/getall', { UserId: userId });
      
      if (response.status === 200) {
        // The API returns data in response.data.data, which should be the array of vehicles
        // If it's nested (array within array), we need to flatten it
        const vehicleArray = response.data.data || [];
        
        // Check if the first element is an array (double-wrapped)
        const finalData = (Array.isArray(vehicleArray) && vehicleArray.length > 0 && Array.isArray(vehicleArray[0])) 
          ? vehicleArray[0] 
          : vehicleArray;
        
        setVehicleData(finalData);
        logAuditEvent(moduleName, 'Fetch Vehicles', `Fetched ${finalData?.length || 0} vehicles`);
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Failed to load vehicles', 'Error', setAlert);
      logAuditEvent(moduleName, 'Fetch Error', `Failed to fetch vehicles: ${error.message}`);
    }
  };

  function openAddModal() {
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add vehicle modal');
  }

  function closeModal() {
    setIsOpen(false);
  }

  async function onSubmit(e, data) {
    if (e.target.name === "Add") {
      const vehicleModel = data;
      if(vehicleModel){
        const formatDate = (d) => {
          if (!d) return null;
          const dateObj = typeof d === 'string' || d instanceof String ? new Date(d) : d;
          if (isNaN(dateObj)) return null;
          return dateObj.toISOString().slice(0, 10);
        };
        let vehicleDetails = {
          VehicleId: null,
          VehicleType: vehicleModel.VehicleType || "",
          Manufacturer: vehicleModel.Manufacturer || "",
          ModelName: vehicleModel.ModelName || "",
          Variant: vehicleModel.Variant || "",
          Segment: vehicleModel.Segment || "",
          Color: vehicleModel.Color || "",
          FuelType: vehicleModel.FuelType || "",
          EngineCC: vehicleModel.EngineCC || "",
          LaunchDate: formatDate(vehicleModel.LaunchDate),
          DiscontinueDate: formatDate(vehicleModel.DiscontinueDate),
          Status: vehicleModel.Status || "",
          Remarks: vehicleModel.Remarks || "",
          UserId: getStorageValues('userId')
        };
        if (validation(vehicleDetails)) {
          try {
            const response = await axios.post(
              getApiBaseUrl() + "/vehicle/save",
              vehicleDetails
            );
            if (response.status === 200) {
              if(response.data.data && response.data.data[0] && response.data.data[0].success === 1){
                CreateAlert(response.data.data[0].message, "Success", setAlert);
                fetchVehicles();
                closeModal();
              } else {
                CreateAlert(response.data.data[0]?.message || "Error adding vehicle", "Error", setAlert);
              }
            } 
          } catch (error) {
            console.log(error);
            CreateAlert("Error adding vehicle", "Error", setAlert);
          }
        }
      } else {
        CreateAlert("Fields marked with an asterisk (*) are required!!", "Error", setAlert);
      }
    } 
  }

  function validation(vehicleDetails) {
    var validation = true;
    if (vehicleDetails) {
      if (!vehicleDetails.VehicleType || vehicleDetails.VehicleType.length === 0) {
        CreateAlert("Please select vehicle type!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.Manufacturer || vehicleDetails.Manufacturer.length === 0) {
        CreateAlert("Please enter manufacturer name!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.ModelName || vehicleDetails.ModelName.length === 0) {
        CreateAlert("Please enter model name!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.Variant || vehicleDetails.Variant.length === 0) {
        CreateAlert("Please select variant!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.Segment || vehicleDetails.Segment.length === 0) {
        CreateAlert("Please select segment!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.Color || vehicleDetails.Color.length === 0) {
        CreateAlert("Please enter color!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.FuelType || vehicleDetails.FuelType.length === 0) {
        CreateAlert("Please select fuel type!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.EngineCC || vehicleDetails.EngineCC.length === 0) {
        CreateAlert("Please select engine CC!!", "Warning", setAlert);
        validation = false;
      } else if (!vehicleDetails.Status || vehicleDetails.Status.length === 0) {
        CreateAlert("Please select status!!", "Warning", setAlert);
        validation = false;
      }
    } else {
      CreateAlert("Some problem fetching vehicle details entered by the user!!", "Error", setAlert);
      return false;
    }
    return validation;
  }

  const handleRefresh = () => {
    fetchVehicles();
  };

  const exportColumns = [
    { name: 'Vehicle ID', selector: 'VehicleId' },
    { name: 'Vehicle Type', selector: 'VehicleType' },
    { name: 'Manufacturer', selector: 'Manufacturer' },
    { name: 'Model', selector: 'ModelName' },
    { name: 'Variant', selector: 'Variant' },
    { name: 'Segment', selector: 'Segment' },
    { name: 'Color', selector: 'Color' },
    { name: 'Fuel Type', selector: 'FuelType' },
    { name: 'Engine CC', selector: 'EngineCC' },
    { name: 'Launch Date', selector: 'LaunchDate' },
    { name: 'Discontinue Date', selector: 'DiscontinueDate' },
    { name: 'Status', selector: 'Status' },
    { name: 'Remarks', selector: 'Remarks' }
  ];

  const handleExportExcel = () => {
    exportToExcel(moduleName, exportColumns, vehicleData || []);
    logAuditEvent(moduleName, 'Export Excel', `Rows=${vehicleData?.length || 0}`);
  };

  const handleExportPDF = () => {
    exportToPDF(moduleName, exportColumns, vehicleData || []);
    logAuditEvent(moduleName, 'Export PDF', `Rows=${vehicleData?.length || 0}`);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={moduleName}
        onModelOpen={openAddModal}
        onRefresh={handleRefresh}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <VehicleGrid
        VehicleData={vehicleData}
        fetchVehicles={fetchVehicles}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <VehicleModal
        show={modalIsOpen}
        UserAction="Add"
        onClose={closeModal}
        selectedRow={new VehicleModel()}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default VehicleMain;
